import { TOOL_DISPLAY_NAMES, type ToolName } from "@/copilot/constants";
import { zrpc } from "@/lib/zrpc";
import type { ToolInvocationUIPart } from "@ai-sdk/ui-utils";
import { QueryClient } from "@tanstack/react-query";
import type { ToolCallDisplayConfig } from "@zapier/ai-toolkit/client";
import { capitalCase } from "change-case";

/**
 * Invalidates React Query cache for interface-related queries.
 * Call this after any operation that modifies interface data to ensure UI stays in sync.
 *
 * @param client - The QueryClient instance
 * @param interfaceId - The ID of the interface that was modified
 */
export async function invalidateInterfaceQueries(
  client: QueryClient,
  interfaceId: string
): Promise<void> {
  const getInterfaceQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}",
    {
      params: {
        path: {
          interface_id: interfaceId,
        },
      },
    }
  );
  const getInterfacesQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces",
    {}
  );

  await Promise.all([
    client.invalidateQueries(getInterfaceQueryOptions),
    client.invalidateQueries(getInterfacesQueryOptions),
  ]);
}

/**
 * Invalidates React Query cache for interface page-related queries.
 * Call this after any operation that modifies page data to ensure UI stays in sync.
 *
 * @param client - The QueryClient instance
 * @param interfaceId - The ID of the interface that was modified
 * @param pageId - The ID of the page that was modified
 */
export async function invalidatePageQueries({
  client,
  interfaceId,
  pageId,
}: {
  client: QueryClient;
  interfaceId: string;
  pageId: string;
}): Promise<void> {
  const listPagesQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}/pages",
    {
      params: {
        path: {
          interface_id: interfaceId,
        },
      },
    }
  );
  const getPageQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}/pages/{page_id}",
    {
      params: {
        path: {
          interface_id: interfaceId,
          page_id: pageId,
        },
      },
    }
  );

  await Promise.all([
    client.invalidateQueries(listPagesQueryOptions),
    client.invalidateQueries(getPageQueryOptions),
  ]);
}

/**
 * Inspired by https://gitlab.com/zapier/zapai/ai-toolkit/-/blob/main/packages/ai-toolkit/src/components/Copilot/CopilotMessagePart/parts/CopilotToolPart.tsx#L57
 */
export function getToolCallDisplay({
  toolCallInvocation,
  /**
   * This was added to make writing tests easier.
   */
  toolNameToDisplayName = defaultToolNameToDisplayName,
}: {
  toolCallInvocation: ToolInvocationUIPart["toolInvocation"];
  toolNameToDisplayName?: (toolName: string) => string;
}): ToolCallDisplayConfig {
  if (hasCustomDisplayMessage(toolCallInvocation.args)) {
    return {
      message: toolCallInvocation.args.displayMessage,
    };
  }

  if (isEmptyPartialCall(toolCallInvocation)) {
    return {
      message: "...",
    };
  }

  const errorMessage = getErrorFromResult(toolCallInvocation);
  if (errorMessage) {
    return {
      message: errorMessage,
      isError: true,
    };
  }

  const displayName = toolNameToDisplayName(toolCallInvocation.toolName);
  if (isToolCallInProgress(toolCallInvocation)) {
    return {
      message: `${displayName} being called...`,
    };
  }

  if (isToolCallCancelled(toolCallInvocation)) {
    return {
      message: `${displayName} stopped`,
    };
  }

  return {
    message: `${displayName} called`,
  };
}

export function defaultToolNameToDisplayName(toolName: string): string {
  const definedDisplayName = TOOL_DISPLAY_NAMES[
    toolName as unknown as ToolName
  ] as string | undefined;

  return definedDisplayName ?? capitalCase(toolName);
}

function hasCustomDisplayMessage(
  args: ToolInvocationUIPart["toolInvocation"]["args"]
): boolean {
  return args.displayMessage !== undefined;
}

function isEmptyPartialCall(
  toolCallInvocation: ToolInvocationUIPart["toolInvocation"]
): boolean {
  return (
    toolCallInvocation.state === "partial-call" &&
    Object.keys(toolCallInvocation.args || {}).length === 0
  );
}

function getErrorFromResult(
  toolCallInvocation: ToolInvocationUIPart["toolInvocation"]
): string | undefined {
  if (toolCallInvocation.state !== "result") {
    return undefined;
  }

  return toolCallInvocation.result?.$error?.message;
}

function isToolCallCancelled(
  toolCallInvocation: ToolInvocationUIPart["toolInvocation"]
): boolean {
  if (toolCallInvocation.state !== "result") {
    return false;
  }

  const resultText = toolCallInvocation.result?.content?.[0]?.text;
  return resultText === "Tool cancelled by user";
}

function isToolCallInProgress(
  toolCallInvocation: ToolInvocationUIPart["toolInvocation"]
): boolean {
  return toolCallInvocation.state !== "result";
}
